import {writable, derived, get} from 'svelte/store';
import {
  type DocumentData,
  query,
  where,
  doc,
  getDoc,
  getDocs,
  type QueryConstraint,
  type Timestamp,
  type QueryDocumentSnapshot
} from 'firebase/firestore';

import {
  ProjectType,
  type DevelopmentProject,
  type ConsultingProject,
  type Project,
  type DevelopmentProjectDraft,
  type ConsultingProjectDraft,
  type ProjectDraft
} from '../types/project';
import type {DateRange} from '../types/date';

import {consultingProjectsDb, developmentProjectsDb} from '../utils/firebase';

import {user as userStore} from './user';

// Convert Firestore Timestamp objects to Javascript Date objects
function convertCreationPeriodTimestampsToDates(creationPeriod?: {
  start: Timestamp | null;
  end: Timestamp | null;
}): DateRange {
  return {
    start: creationPeriod?.start ? creationPeriod.start.toDate() : null,
    end: creationPeriod?.end ? creationPeriod.end.toDate() : null
  };
}

// Returns a project object with id, type and Javascript dates
function getProjectData(
  documentSnapshot: QueryDocumentSnapshot<DocumentData>,
  type: ProjectType
): Project | ProjectDraft {
  const rawProjectData = documentSnapshot.data();
  const project = {
    ...rawProjectData,
    id: documentSnapshot.id,
    ...(rawProjectData.creationPeriod && {
      creationPeriod: convertCreationPeriodTimestampsToDates(
        rawProjectData.creationPeriod
      )
    }),
    type
  };

  return project;
}

async function fetchProjects(
  projectId?: string
): Promise<Map<string, Project | ProjectDraft>> {
  const user = get(userStore);
  const projects = new Map();

  // Fetch only one project if a project id is passed
  if (projectId) {
    const errors = [];

    try {
      const developmentProjectQuerySnapshot = await getDoc(
        doc(developmentProjectsDb, projectId)
      );

      if (developmentProjectQuerySnapshot.exists()) {
        projects.set(
          developmentProjectQuerySnapshot.id,
          getProjectData(
            developmentProjectQuerySnapshot,
            ProjectType.DEVELOPMENT
          )
        );
      }
    } catch (error) {
      errors.push(error);
    }

    try {
      const consultingProjectQuerySnapshot = await getDoc(
        doc(consultingProjectsDb, projectId)
      );

      if (consultingProjectQuerySnapshot.exists()) {
        projects.set(
          consultingProjectQuerySnapshot.id,
          getProjectData(consultingProjectQuerySnapshot, ProjectType.CONSULTING)
        );
      }
    } catch (error) {
      errors.push(error);
    }

    // One error is expected here, since the project id exists only for a
    // development OR consulting project, so one of the request always fails
    if (errors.length > 1) {
      errors.forEach(console.error);
    }
  } else {
    const queryConstraints: Array<QueryConstraint> = !user
      ? // Fetch only public projects if the user is not logged in
        [where('visibility', '==', 'Public'), where('isDraft', '==', false)]
      : [];

    try {
      const developmentProjectsQuerySnapshot = await getDocs(
        query(developmentProjectsDb, ...queryConstraints)
      );

      developmentProjectsQuerySnapshot.forEach(doc => {
        projects.set(doc.id, getProjectData(doc, ProjectType.DEVELOPMENT));
      });
    } catch (error) {
      console.error(error);
    }

    try {
      const consultingProjectsQuerySnapshot = await getDocs(
        query(consultingProjectsDb, ...queryConstraints)
      );

      consultingProjectsQuerySnapshot.forEach(doc => {
        projects.set(doc.id, getProjectData(doc, ProjectType.CONSULTING));
      });
    } catch (error) {
      console.error(error);
    }
  }

  return projects;
}

function createProjects() {
  const initialValue = new Map();
  const {subscribe, set, update} =
    writable<Map<string, Project | ProjectDraft>>(initialValue);

  return {
    subscribe,
    fetchAllProjects: async () => {
      const fetchedProjects = await fetchProjects();
      set(fetchedProjects);
    },
    fetchProject: async (projectId: string) => {
      const fetchedProjects = await fetchProjects(projectId);
      update(storeProjects => new Map([...storeProjects, ...fetchedProjects]));
    },
    clear: () => set(initialValue)
  };
}

// All projects (development and consulting) by id
export const projects = createProjects();

// Returns a projects map (projects by id) filtered by project type
const filterProjectsByType =
  <T extends Project | ProjectDraft>(projectType: ProjectType) =>
  ($projects: Map<string, Project | ProjectDraft>) =>
    new Map([...$projects].filter(([, {type}]) => type === projectType)) as Map<
      string,
      T
    >;
// All development projects by id
export const developmentProjects = derived(
  projects,
  filterProjectsByType<DevelopmentProject | DevelopmentProjectDraft>(
    ProjectType.DEVELOPMENT
  )
);
// All consulting projects by id
export const consultingProjects = derived(
  projects,
  filterProjectsByType<ConsultingProject | ConsultingProjectDraft>(
    ProjectType.CONSULTING
  )
);

// Returns a unique list of values for a given project property in alphabetical order (case insensitive)
const getPropertyValues =
  <T extends Project | ProjectDraft, P extends keyof T>(
    propertyName: P | Array<P>
  ) =>
  ($projects: Map<string, T>) => {
    const propertyNames = Array.isArray(propertyName)
      ? propertyName
      : [propertyName];
    return [
      ...new Set(
        [...$projects.values()]
          .map(project =>
            propertyNames.map(property => project[property]).flat()
          )
          .flat()
          .filter(Boolean as any as <X>(x: X) => x is NonNullable<X>)
      )
    ].sort((a, b) =>
      String(a).localeCompare(String(b), undefined, {sensitivity: 'base'})
    );
  };

// Get a unique list of customers used in the projects in alphabetical order
export const customers = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'customers'>('customers')
);

// Get a unique list of project leads used in the projects in alphabetical order
export const projectLead = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'projectLead'>('projectLead')
);

// Get a unique list of team members and project leads used in the projects in alphabetical order
export const teamAndProjectLeads = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'team' | 'projectLead'>([
    'team',
    'projectLead'
  ])
);

// Get a unique list of fields used in the projects in alphabetical order
export const fields = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'fields'>('fields')
);

// Get a unique list of topics used in the projects in alphabetical order
export const topics = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'topics'>('topics')
);

// Get a unique list of services used in the projects in alphabetical order
export const services = derived(
  projects,
  getPropertyValues<Project | ProjectDraft, 'services'>('services')
);

// Get a unique list of tasks used in the development projects in alphabetical order
export const tasks = derived(
  developmentProjects,
  getPropertyValues<DevelopmentProject | DevelopmentProjectDraft, 'tasks'>(
    'tasks'
  )
);

// Get a unique list of technologies used in the development projects in alphabetical order
export const technologies = derived(
  developmentProjects,
  getPropertyValues<
    DevelopmentProject | DevelopmentProjectDraft,
    'technologies'
  >('technologies')
);

// Get a unique list of methods used in the consulting projects in alphabetical order
export const methods = derived(
  consultingProjects,
  getPropertyValues<ConsultingProject | ConsultingProjectDraft, 'methods'>(
    'methods'
  )
);

// Get a unique list of formats used in the consulting projects in alphabetical order
export const formats = derived(
  consultingProjects,
  getPropertyValues<ConsultingProject | ConsultingProjectDraft, 'formats'>(
    'formats'
  )
);
